import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { GroupLinkPreviewDto } from '@mondy/shared';
import { ApiError } from '@mondy/shared/api-client';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useGroupLinkPreview } from '@/hooks/useGroupLinkPreview';
import { useRequestToJoin } from '@/hooks/useRequestToJoin';
import { useSetActiveGroup } from '@/state/activeGroup';
import { Spinner } from '@/components/Spinner';

/**
 * `/join/:shortId` — the public landing page for shortId invite links.
 * Unauthed users see a greeting with log-in / sign-up CTAs. Authed non-members
 * get a "Request to join" button. Pending users see a "waiting" state. Members
 * get a shortcut to the standings.
 */
export function JoinGroupPage() {
  const { shortId } = useParams<{ shortId: string }>();
  const preview = useGroupLinkPreview(shortId ?? null);
  const user = useCurrentUser();

  if (!shortId) {
    return (
      <StaticMessage
        title="Invalid invite link"
        body="This link is missing its group id."
      />
    );
  }

  if (preview.isLoading || user === undefined) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md items-center justify-center px-6 py-8">
        <Spinner label="Loading invite" />
      </main>
    );
  }

  if (preview.isError) {
    const err = preview.error;
    if (err instanceof ApiError && err.status === 404) {
      return (
        <StaticMessage
          title="Invite link invalid"
          body="This invite link is invalid. Ask the group owner to share a new one."
        />
      );
    }
    return (
      <StaticMessage
        title="Something went wrong"
        body="Could not load this invite. Try again in a moment."
      />
    );
  }

  const data = preview.data!.preview;
  const kind = data.viewerState.kind;

  if (kind === 'anonymous' || !user) {
    return <AnonymousView shortId={shortId} preview={data} />;
  }
  if (kind === 'member') {
    return <AlreadyMemberView preview={data} />;
  }
  if (kind === 'pending') {
    return <PendingView preview={data} />;
  }
  // kind === 'none' — authed, not yet a member, no pending request
  return <RequestJoinView shortId={shortId} preview={data} />;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col px-6 py-8">
      <Link
        to="/"
        className="mb-10 flex items-center gap-2 text-sm text-[color:var(--color-fg-muted)]"
      >
        <span aria-hidden>←</span> Home
      </Link>
      <div className="flex flex-1 flex-col justify-center gap-6">{children}</div>
    </main>
  );
}

function StaticMessage({ title, body }: { title: string; body: string }) {
  return (
    <Shell>
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold">{title}</h1>
        <p className="text-sm text-[color:var(--color-fg-muted)]">{body}</p>
      </header>
      <Link
        to="/"
        className="h-11 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-4 text-center font-medium leading-[44px]"
      >
        Go home
      </Link>
    </Shell>
  );
}

function MemberCountLine({ preview }: { preview: GroupLinkPreviewDto }) {
  return (
    <p className="text-sm text-[color:var(--color-fg-muted)]">
      {preview.memberCount} member{preview.memberCount === 1 ? '' : 's'} already
      in.
    </p>
  );
}

function AnonymousView({
  shortId,
  preview,
}: {
  shortId: string;
  preview: GroupLinkPreviewDto;
}) {
  const next = `/join/${shortId}`;
  const loginParams = new URLSearchParams({ next }).toString();
  const signupParams = new URLSearchParams({ next }).toString();
  return (
    <Shell>
      <header className="flex flex-col gap-2">
        <span className="text-xs uppercase tracking-[0.22em] text-[color:var(--color-fg-muted)]">
          Group invite
        </span>
        <h1 className="text-2xl font-semibold">
          {preview.ownerDisplayName} invited you to join{' '}
          <span style={{ color: 'var(--hot)' }}>{preview.name}</span>.
        </h1>
        <MemberCountLine preview={preview} />
      </header>
      <div className="flex flex-col gap-3">
        <Link
          to={`/login?${loginParams}`}
          className="h-11 rounded-xl bg-[color:var(--color-accent)] px-4 text-center font-medium leading-[44px] text-[color:var(--color-accent-fg)]"
        >
          Log in
        </Link>
        <Link
          to={`/signup?${signupParams}`}
          className="h-11 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-4 text-center font-medium leading-[44px]"
        >
          Create an account
        </Link>
      </div>
    </Shell>
  );
}

function RequestJoinView({
  shortId,
  preview,
}: {
  shortId: string;
  preview: GroupLinkPreviewDto;
}) {
  const qc = useQueryClient();
  const request = useRequestToJoin(shortId);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onRequest() {
    setError(null);
    try {
      await request.mutateAsync();
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409 && err.code === 'ALREADY_MEMBER') {
          // Server says we're in — refetch the preview so the UI flips.
          void qc.invalidateQueries({
            queryKey: ['groups', 'by-short-id', shortId],
          });
          return;
        }
        setError(err.message || 'Could not send request.');
        return;
      }
      setError('Could not send request.');
    }
  }

  if (submitted) {
    return (
      <Shell>
        <header className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold">Request sent.</h1>
          <p className="text-sm text-[color:var(--color-fg-muted)]">
            {preview.ownerDisplayName} will approve or reject your request
            shortly. You'll see{' '}
            <b className="text-[color:var(--color-fg)]">{preview.name}</b> in
            your groups once they approve.
          </p>
        </header>
        <Link
          to="/"
          className="h-11 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-4 text-center font-medium leading-[44px]"
        >
          Back to home
        </Link>
      </Shell>
    );
  }

  return (
    <Shell>
      <header className="flex flex-col gap-2">
        <span className="text-xs uppercase tracking-[0.22em] text-[color:var(--color-fg-muted)]">
          Group invite
        </span>
        <h1 className="text-2xl font-semibold">
          {preview.ownerDisplayName} invited you to join{' '}
          <span style={{ color: 'var(--hot)' }}>{preview.name}</span>.
        </h1>
        <MemberCountLine preview={preview} />
      </header>
      <button
        type="button"
        onClick={onRequest}
        disabled={request.isPending}
        className="h-11 rounded-xl bg-[color:var(--color-accent)] px-4 font-medium text-[color:var(--color-accent-fg)] disabled:pointer-events-none disabled:opacity-60"
      >
        {request.isPending ? 'Sending…' : 'Request to join'}
      </button>
      {error ? (
        <p className="text-sm text-[color:var(--color-danger)]">{error}</p>
      ) : null}
    </Shell>
  );
}

function PendingView({ preview }: { preview: GroupLinkPreviewDto }) {
  return (
    <Shell>
      <header className="flex flex-col gap-2">
        <span className="text-xs uppercase tracking-[0.22em] text-[color:var(--color-fg-muted)]">
          Pending
        </span>
        <h1 className="text-2xl font-semibold">
          Waiting on{' '}
          <span style={{ color: 'var(--hot)' }}>{preview.ownerDisplayName}</span>
          .
        </h1>
        <p className="text-sm text-[color:var(--color-fg-muted)]">
          Your request to join <b>{preview.name}</b> has been sent.
        </p>
      </header>
      <Link
        to="/"
        className="h-11 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-4 text-center font-medium leading-[44px]"
      >
        Back to home
      </Link>
    </Shell>
  );
}

function AlreadyMemberView({ preview }: { preview: GroupLinkPreviewDto }) {
  const setActiveGroupId = useSetActiveGroup();
  const navigate = useNavigate();

  function onGo() {
    setActiveGroupId(preview.groupId);
    navigate('/standings');
  }

  return (
    <Shell>
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold">
          You're already in{' '}
          <span style={{ color: 'var(--hot)' }}>{preview.name}</span>.
        </h1>
        <p className="text-sm text-[color:var(--color-fg-muted)]">
          Jump straight to the table.
        </p>
      </header>
      <button
        type="button"
        onClick={onGo}
        className="h-11 rounded-xl bg-[color:var(--color-accent)] px-4 font-medium text-[color:var(--color-accent-fg)]"
      >
        Go to standings
      </button>
    </Shell>
  );
}
